import { useState, useCallback } from 'react';
import { Button } from 'reactstrap';
import { saveDownloadedFile } from 'common/utils/downloads';
import { icon } from '@/library/utils/files.ts';
import { isFileSynced } from '@/api/files/api';
import { useAppSelector } from '@/store/hooks';
import { Attachment } from '@/library/types/files';

import useUpdateEffect from 'common/utils/hooks/useUpdateEffect';
import useGallery from '@/library/components/gallery/useGallery';
import Gallery from '@/library/components/gallery';
import SvgRender from 'common/components/general/SvgRender';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import useTooltipID from 'common/utils/hooks/useTooltipID';

import Spinner from 'common/components/general/Spinner';
import AttachmentsMenu from 'common/components/attachments/AttachmentsMenu';
import AttachmentFile from 'common/components/attachments/AttachmentFile';
import config, { DownloadAttachmentSizeType } from '@/common/components/attachments/config';

type DownloadAttachmentData = {
  id?: number | string;
  attachments_count?: number;
  attachments?: Attachment[];
  /** Legacy shape used by some callers instead of `attachments`. */
  data?: Attachment[] | null;
};

type DownloadAttachmentProps = {
  hasAttachments: boolean;
  isDisabled?: boolean;
  isHidden?: boolean;
  hiddenSuffix?: boolean;
  downloadFileName?: string | null;
  canAddAttachments?: boolean;
  size?: DownloadAttachmentSizeType;
  popupPlacement?: 'left' | 'right';
  data: DownloadAttachmentData;
  attachFiles?: () => void;
  downloadFiles?: (data: { id?: number | string }) => Promise<unknown> | unknown;
  requestAttachments?: () => Promise<unknown> | unknown;
};

const DownloadAttachment = ({
  hasAttachments = false,
  downloadFiles,
  attachFiles = () => {},
  data = {},
  isDisabled = false,
  isHidden = false,
  downloadFileName = null,
  hiddenSuffix = false,
  canAddAttachments = true,
  popupPlacement = 'left',
  requestAttachments,
  size = 'sm'
}: DownloadAttachmentProps) => {
  const isOnBoard = useAppSelector(state => state.isOnBoard);
  const { tooltipID, avoidRender } = useTooltipID('download-attachment-btn');

  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [attachments, setAttachments] = useState(_get(data, 'attachments') || []);
  const [canDownloadAllAttachments, setCanDownloadAllAttachments] = useState(false);

  const {
    setGalleryFiles,
    toggleGallery,
    setGalleryCurrentFile,
    setGalleryRotateDirection,
    updateGalleryFile,
    ...galleryState
  } = useGallery();

  useUpdateEffect(() => {
    setIsAttachmentMenuOpen(false);
    if (!_isEqual(data.attachments, attachments)) {
      setAttachments(data.attachments || []);
    }
  }, [data.id, data.attachments]);

  const getConfig = useCallback(() => {
    if (hasAttachments && isAttachmentMenuOpen) {
      return config.close;
    } else if (
      !attachments.length &&
      !data.attachments_count &&
      hasAttachments &&
      canAddAttachments &&
      downloadFiles
    ) {
      return config.download;
    } else if (hasAttachments) {
      return config.attachments;
    } else if (canAddAttachments) {
      return config.add;
    } else {
      return config.attachments;
    }
  }, [
    hasAttachments,
    canAddAttachments,
    isAttachmentMenuOpen,
    attachments.length,
    data.attachments_count,
    downloadFiles
  ]);

  const previewAttachmentHandler = (event: React.MouseEvent<HTMLElement>, file: Attachment) => {
    event.stopPropagation();

    setGalleryFiles(attachments);
    toggleGallery(true);
    setGalleryCurrentFile(file);
  };

  const onClick = async () => {
    if (hasAttachments) {
      if (!isAttachmentMenuOpen) {
        let menuAttachments: Attachment[] = [];

        if (requestAttachments) {
          setIsLoading(true);

          try {
            const res = await requestAttachments();

            menuAttachments = Array.isArray(res)
              ? res
              : (res as { data?: Attachment[] } | undefined)?.data || [];
          } catch (err) {
            console.error(err);
          }

          setIsLoading(false);
        } else {
          menuAttachments = _get(data, 'attachments') || [];
        }

        setAttachments(menuAttachments);
        setCanDownloadAllAttachments(
          menuAttachments.every((file: Attachment) => isFileSynced(file, isOnBoard).is_synced)
        );
      }

      setIsAttachmentMenuOpen(!isAttachmentMenuOpen);
    } else {
      attachFiles();
    }
  };

  const downloadHandler = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (hasAttachments) {
      if (!canDownloadAllAttachments && isOnBoard) return;

      setIsDownloading(true);

      if (downloadFiles) {
        const response = await downloadFiles({ id: data.id });

        const downloadResponse = response as { data?: Blob } | undefined;
        if (downloadResponse?.data) {
          saveDownloadedFile(
            downloadResponse,
            `${downloadFileName ? downloadFileName : ''}${hiddenSuffix ? '' : '_attachments'}`
          );
        }
      }

      setIsDownloading(false);
    } else {
      attachFiles();
    }
  };

  const selectedConfig = getConfig();
  const showDownloadAllButton = !!downloadFiles;
  const attachmentsCount = attachments.length || data.attachments_count || 0;
  const minimumVisibleAttachments = requestAttachments ? 0 : 1;

  if (avoidRender) return null;

  return !isHidden ? (
    <div className="d-inline-flex align-items-center justify-content-center position-relative download-attachment-wrapper">
      {!isLoading ? (
        attachmentsCount > minimumVisibleAttachments ? (
          <>
            <Button
              type="button"
              color="white"
              id={tooltipID}
              className={`download-attachment-btn p-0 d-inline-flex align-items-center justify-content-center ${
                selectedConfig.className
              } ${attachmentsCount > 0 || canAddAttachments ? '' : 'shadow-none disabled'}`}
              onClick={selectedConfig.enableDownload ? downloadHandler : onClick}
              size={size}
            >
              {(!canAddAttachments || attachmentsCount) && !isAttachmentMenuOpen ? (
                <div
                  className={`${
                    size === 'xl' || size === 'xxl' ? 'fs-14' : 'fs-10'
                  } text-moody-blue fw-bold ${
                    attachmentsCount >= 10 && size === 'sm' ? '' : 'cme-2'
                  } ${attachmentsCount > 0 || canAddAttachments ? '' : 'disabled'}`}
                >
                  {attachmentsCount || 0}
                </div>
              ) : null}

              {attachmentsCount >= 10 &&
              size === 'sm' &&
              !selectedConfig.className?.includes('close') ? null : (
                <SvgRender
                  {...selectedConfig.icon}
                  style={
                    size !== 'xl' && size !== 'xxl'
                      ? selectedConfig.icon.style
                      : {
                          width: selectedConfig.icon.style.width + 5,
                          height: selectedConfig.icon.style.height + 5
                        }
                  }
                />
              )}
            </Button>
            {attachments.length ? (
              <AttachmentsMenu
                isAttachmentMenuOpen={isAttachmentMenuOpen}
                downloadHandler={downloadHandler}
                closeMenu={() => setIsAttachmentMenuOpen(false)}
                isDisabled={isDisabled || isDownloading}
                attachments={attachments}
                previewAttachmentHandler={previewAttachmentHandler}
                popupPlacement={popupPlacement}
                canDownloadAllAttachments={canDownloadAllAttachments}
                showDownloadAllButton={showDownloadAllButton}
                size={size}
                targetId={tooltipID}
              />
            ) : null}
          </>
        ) : (
          <>
            {attachments.map((file, index) => (
              <AttachmentFile
                key={file.id}
                file={file}
                onClick={previewAttachmentHandler}
                size={size}
                icon={icon(file.mime_type, file.extension)}
                index={index}
              />
            ))}
          </>
        )
      ) : (
        <div className="cpt-2">
          <Spinner size={size === 'lg' ? 28 : 16} />
        </div>
      )}
      <Gallery
        setRotateDirection={setGalleryRotateDirection}
        setSelectedFile={setGalleryCurrentFile}
        toggleGallery={toggleGallery}
        setGalleryFiles={setGalleryFiles}
        updateGalleryFile={updateGalleryFile}
        {...galleryState}
      />
    </div>
  ) : null;
};

export default DownloadAttachment;
